import { Link } from "react-router-dom";
import { Shield, Lock, Eye, Database, Users, Bell, Cookie, Mail } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="py-12 md:py-20 bg-gray-50 border-b-2 border-gray-200">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-center mb-6">
            <div className="p-4 bg-yellow-100 rounded-full">
              <Lock className="w-12 h-12 text-yellow-600" />
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-center mb-4 text-black">
            Privacy Policy
          </h1>
          <p className="text-gray-600 text-center text-lg">
            Last Updated: January 5, 2026
          </p>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-16">
        
        {/* Introduction */}
        <section className="mb-12">
          <div className="flex items-center space-x-3 mb-4">
            <Shield className="w-6 h-6 text-yellow-600" />
            <h2 className="text-2xl md:text-3xl font-bold text-black">
              Introduction
            </h2>
          </div>
          <p className="text-gray-700 leading-relaxed mb-4">
            At Hawkins & Brooks, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
          </p>
          <p className="text-gray-700 leading-relaxed">
            By using our website, you consent to the data practices described in this policy. If you do not agree with this policy, please do not access or use our services.
          </p>
        </section>

        {/* Information We Collect */}
        <section className="mb-12">
          <div className="flex items-center space-x-3 mb-4">
            <Database className="w-6 h-6 text-yellow-600" />
            <h2 className="text-2xl md:text-3xl font-bold text-black">
              Information We Collect
            </h2>
          </div>
          
          <h3 className="text-xl font-semibold text-black mb-3 mt-6">Personal Information</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            We may collect personal information that you voluntarily provide to us when you:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-6">
            <li>Fill out contact forms or request consultations</li>
            <li>Subscribe to our newsletters or updates</li>
            <li>Register for events or webinars</li>
            <li>Communicate with us via email or phone</li>
            <li>Apply for employment opportunities</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mb-4">
            This information may include:
          </p>
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
              <p className="text-gray-700">• Name and contact details</p>
              <p className="text-gray-700">• Email address</p>
              <p className="text-gray-700">• Phone number</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
              <p className="text-gray-700">• Company name and position</p>
              <p className="text-gray-700">• Business requirements</p>
              <p className="text-gray-700">• Payment information</p>
            </div>
          </div>

          <h3 className="text-xl font-semibold text-black mb-3 mt-6">Automatically Collected Information</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            When you visit our website, we may automatically collect certain information about your device, including:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
            <li>IP address and browser type</li>
            <li>Operating system and device information</li>
            <li>Pages visited and time spent on pages</li>
            <li>Referring website addresses</li>
            <li>Clickstream data and interaction patterns</li>
          </ul>
        </section>

        {/* How We Use Your Information */}
        <section className="mb-12">
          <div className="flex items-center space-x-3 mb-4">
            <Eye className="w-6 h-6 text-yellow-600" />
            <h2 className="text-2xl md:text-3xl font-bold text-black">
              How We Use Your Information
            </h2>
          </div>
          <p className="text-gray-700 leading-relaxed mb-4">
            We use the information we collect to:
          </p>
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
              <p className="text-gray-700 leading-relaxed">Provide, operate, and maintain our services</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
              <p className="text-gray-700 leading-relaxed">Respond to your inquiries and provide customer support</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
              <p className="text-gray-700 leading-relaxed">Send administrative information, updates, and notifications</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
              <p className="text-gray-700 leading-relaxed">Process transactions and send transaction-related communications</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
              <p className="text-gray-700 leading-relaxed">Improve our website and services based on user feedback</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
              <p className="text-gray-700 leading-relaxed">Send marketing communications (with your consent)</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
              <p className="text-gray-700 leading-relaxed">Prevent fraudulent activities and enhance security</p>
            </div>
          </div>
        </section>

        {/* Cookies and Tracking */}
        <section className="mb-12">
          <div className="flex items-center space-x-3 mb-4">
            <Cookie className="w-6 h-6 text-yellow-600" />
            <h2 className="text-2xl md:text-3xl font-bold text-black">
              Cookies and Tracking Technologies
            </h2>
          </div>
          <p className="text-gray-700 leading-relaxed mb-4">
            We use cookies and similar tracking technologies to track activity on our website and store certain information. Cookies are files with small amounts of data that may include an anonymous unique identifier.
          </p>
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded mb-4">
            <p className="text-gray-700 leading-relaxed font-semibold mb-2">
              Types of Cookies We Use:
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-700 mt-2 ml-4">
              <li><strong>Essential Cookies:</strong> Required for website functionality</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
              <li><strong>Marketing Cookies:</strong> Track visitors across websites for advertising</li>
            </ul>
          </div>
          <p className="text-gray-700 leading-relaxed">
            You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our website.
          </p>
        </section>

        {/* Information Sharing */}
        <section className="mb-12">
          <div className="flex items-center space-x-3 mb-4">
            <Users className="w-6 h-6 text-yellow-600" />
            <h2 className="text-2xl md:text-3xl font-bold text-black">
              Information Sharing and Disclosure
            </h2>
          </div>
          <p className="text-gray-700 leading-relaxed mb-4">
            We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
          </p>
          <ul className="space-y-3">
            <li className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
              <strong className="text-yellow-600">Service Providers:</strong>
              <p className="text-gray-700 mt-1">We may share information with trusted third-party service providers who assist us in operating our website and conducting our business.</p>
            </li>
            <li className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
              <strong className="text-yellow-600">Legal Requirements:</strong>
              <p className="text-gray-700 mt-1">We may disclose information if required to do so by law or in response to valid requests by public authorities.</p>
            </li>
            <li className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
              <strong className="text-yellow-600">Business Transfers:</strong>
              <p className="text-gray-700 mt-1">In the event of a merger, acquisition, or sale of assets, your information may be transferred.</p>
            </li>
          </ul>
        </section>

        {/* Data Security */}
        <section className="mb-12">
          <div className="flex items-center space-x-3 mb-4">
            <Lock className="w-6 h-6 text-yellow-600" />
            <h2 className="text-2xl md:text-3xl font-bold text-black">
              Data Security
            </h2>
          </div>
          <p className="text-gray-700 leading-relaxed mb-4">
            We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border-l-4 border-yellow-500 pl-4 bg-gray-50 p-3 rounded">
              <p className="text-gray-700">• SSL/TLS encryption</p>
              <p className="text-gray-700">• Secure server infrastructure</p>
              <p className="text-gray-700">• Regular security audits</p>
            </div>
            <div className="border-l-4 border-yellow-500 pl-4 bg-gray-50 p-3 rounded">
              <p className="text-gray-700">• Access controls and authentication</p>
              <p className="text-gray-700">• Data backup procedures</p>
              <p className="text-gray-700">• Employee training on data protection</p>
            </div>
          </div>
        </section>

        {/* Your Rights */}
        <section className="mb-12">
          <div className="flex items-center space-x-3 mb-4">
            <Bell className="w-6 h-6 text-yellow-600" />
            <h2 className="text-2xl md:text-3xl font-bold text-black">
              Your Data Protection Rights
            </h2>
          </div>
          <p className="text-gray-700 leading-relaxed mb-4">
            Depending on your location, you may have the following rights regarding your personal information:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
            <li><strong>Right to Access:</strong> Request copies of your personal information</li>
            <li><strong>Right to Rectification:</strong> Request correction of inaccurate information</li>
            <li><strong>Right to Erasure:</strong> Request deletion of your personal information</li>
            <li><strong>Right to Restrict Processing:</strong> Request limitation of processing your information</li>
            <li><strong>Right to Data Portability:</strong> Request transfer of your data to another organization</li>
            <li><strong>Right to Object:</strong> Object to our processing of your personal information</li>
          </ul>
        </section>

        {/* Data Retention */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
            Data Retention
          </h2>
          <p className="text-gray-700 leading-relaxed">
            We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your information, we will securely delete or anonymize it.
          </p>
        </section>

        {/* Third-Party Links */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
            Third-Party Links
          </h2>
          <p className="text-gray-700 leading-relaxed">
            Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
          </p>
        </section>

        {/* Children's Privacy */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
            Children's Privacy
          </h2>
          <p className="text-gray-700 leading-relaxed">
            Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
          </p>
        </section>

        {/* Changes to Policy */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
            Changes to This Privacy Policy
          </h2>
          <p className="text-gray-700 leading-relaxed">
            We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
          </p>
        </section>

        {/* Contact Section */}
        <section className="bg-black rounded-2xl p-8">
          <div className="flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-yellow-500" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-4">
            Contact Us About Privacy
          </h2>
          <p className="text-gray-300 text-center mb-6">
            If you have any questions or concerns about this Privacy Policy or our data practices, please contact us.
          </p>
          <div className="flex justify-center">
            <Link
              to="/contact"
              className="inline-block px-8 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg transition-all duration-300"
            >
              Contact Us
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
